import 'react-datepicker/dist/react-datepicker.css';

import * as FileSaver from 'file-saver';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

import SearchImg from '../../images/SideBar/Search';
import { useDebounce } from '../../util/useDebounce';
import ConfirmationDialog from '../DialogBox';
import CustomDropdown from '../DropDown/index';
import { week } from './MockData';
import { CommonDataGridProps } from './props';

const CommonDataGrid: React.FC<CommonDataGridProps> = ({
  rows,
  columns: initialColumns,
  getRowId,
  showConfirmation,
  confirmationMessage,
  onCancelDelete,
  onConfirmDelete,
  loading,
  toolbarQuickFilter = true,
  onDateFilter,
  onAllData = () => {},
  addButton = false,
  setSearchBar,
  setCurrentPage,
  currentPage,
  totalPages,
  tableName,
  dayFilter = true,
  mainStyle,
  downloadCSVFilter = true,
  searchFilter = true,
  defaultDateNumber = "1",
  noticePeriodOptions,
  noticePeriodFilter,
  onNoticePeriodFilter,
  ...rest
}) => {
  const [sortValue, setSortValue] = useState<string>(defaultDateNumber);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [showDownloadDatePicker, setShowDownloadDatePicker] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const columns = initialColumns;
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchData, setSearchData] = useState<string>("");

  useDebounce(() => {
    setSearchQuery(searchData ? searchData : "");
    setSearchBar(searchData ? searchData : "");
  }, 1000);

  useEffect(() => {
    if (onDateFilter) {
      const now = new Date();
      if (sortValue === "1") {
        onDateFilter(null, null);
      } else if (sortValue === "2") {
        onDateFilter(now, now);
      } else if (sortValue === "3") {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        onDateFilter(tomorrow, tomorrow);
      } else if (sortValue === "4") {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        onDateFilter(yesterday, yesterday);
      } else if (sortValue === "5") {
        const last7Days = new Date(now);
        last7Days.setDate(last7Days.getDate() - 7);
        onDateFilter(last7Days, now);
      } else if (sortValue === "8") {
        const now = new Date();
        const dayOfWeek = now.getDay();
        const sunday = new Date(now);
        sunday.setDate(now.getDate() - dayOfWeek);
        const saturday = new Date(sunday);
        saturday.setDate(sunday.getDate() + 6);
        onDateFilter(sunday, saturday);
      }else if (sortValue === "9") {
        const now = new Date();
        const currentSunday = new Date(now);
        currentSunday.setDate(now.getDate() - currentSunday.getDay());
        const lastWeekStart = new Date(currentSunday);
        lastWeekStart.setDate(currentSunday.getDate() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        onDateFilter(lastWeekStart, lastWeekEnd);
      } else if (sortValue === "10") {
        const now = new Date();
        const currentSunday = new Date(now);
        currentSunday.setDate(now.getDate() - currentSunday.getDay());
        const nextWeekStart = new Date(currentSunday);
        nextWeekStart.setDate(currentSunday.getDate() + 7);
        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        onDateFilter(nextWeekStart, nextWeekEnd);
      } else if (sortValue === "6") {
        const now = new Date();
        const firstDateLastMonth = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1
        );
        const lastDateLastMonth = new Date(
          now.getFullYear(),
          now.getMonth(),
          0
        );
        onDateFilter(firstDateLastMonth, lastDateLastMonth);
      } else if (sortValue === "7") {
        setShowCustomDateModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortValue, currentPage, searchQuery]);

  const handleDateChange = (start: Date | null, end: Date | null) => {
    setFromDate(start);
    setToDate(end);
    if (start && end && onDateFilter) {
      onDateFilter(start, end);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage ?? 1);
    }
  };

  const handleDownloadClick = () => {
    setShowDownloadDatePicker(true);
  };

  const handleDownloadCSV = async (start: Date | null, end: Date | null) => {
    try {
      const data = await fetchAllData(start, end);
      const csvData = convertDataToCSV(data);
      downloadCSV(csvData, `${tableName}.csv`);
      setShowDownloadDatePicker(false);
      setTimeout(() => {
        if (onDateFilter) {
          onDateFilter(null, null);
        }
      }, 1000);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    }
  };

  const fetchAllData = async (start: Date | null, end: Date | null) => {
    if (onDateFilter) {
      onDateFilter(start, end);
    }
    return rows;
  };

  const convertDataToCSV = (data: any[]): string => {
    if (!data || data.length === 0) {
      return "";
    }

    const processValue = (value: any): string => {
      if (typeof value === "object" && value !== null) {
        if (Array.isArray(value)) {
          return "";
        }
        return "";
      }
      return value === null || value === undefined ? "" : String(value);
    };

    const headerSet = new Set<string>();
    data.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const processedValue = processValue(row[key]);
        if (processedValue !== "") {
          headerSet.add(key);
        }
      });
    });

    const header = Array.from(headerSet);

    const csvRows = data.map((row) => {
      return header
        .map((fieldName) => {
          return JSON.stringify(processValue(row[fieldName]));
        })
        .join(",");
    });

    csvRows.unshift(header.join(","));

    return csvRows.join("\n");
  };

  const downloadCSV = (csvData: string, filename: string) => {
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, filename);
  };

  const handleCloseCustomDateModal = () => {
    setShowCustomDateModal(false);
    setSortValue("1");
  };

  return (
    <div
      className={` p-3 rounded-lg bg-transparent  ${mainStyle} h-[75vh] overflow-auto`}
    >
      <div
        className={`flex justify-between  items-center mb-4 p-2 ${
          addButton === true ? "mr-13" : ""
        }`}
      >
        <h6 className="text-lg font-semibold text-fontBlack">
          {tableName ? tableName : `Table`}
        </h6>
        <div className="flex items-center space-x-2">
          <div className={`flex gap-2 `}>
            {dayFilter === true && (
              <CustomDropdown
                label=""
                options={week}
                defaultOptionId={sortValue}
                setDefaultOptionId={setSortValue}
                displayProperty="name"
                value={sortValue}
                style="flex items-center border h-12 rounded-lg shadow-none relative text-sm border-border text-fontColor"
              />
            )}
            {searchFilter && (
              <div className="flex items-center border h-12 w-[400px] rounded-lg relative text-sm border-border text-fontColor hover:border-2 hover:border-gray-400">
                <div className="ml-4">
                  <SearchImg />
                </div>
                <input
                  type="search"
                  className="w-full pl-3 py-1 rounded-md focus:outline-none bg-transparent"
                  placeholder="Search..."
                  value={searchData}
                  onChange={(e) => setSearchData(e.target.value)}
                />
              </div>
            )}
            {downloadCSVFilter && (
              <button
                className="px-3 py-1 border border-gray-300 rounded-lg text-fontColor font-medium bg-white hover:bg-gray-100"
                onClick={handleDownloadClick}
              >
                CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {showDownloadDatePicker && (
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold text-fontBlack mb-2">
              Select Date Range
            </h3>
            <div className="flex space-x-2">
              <DatePicker
                selected={fromDate}
                onChange={(date: any) => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                placeholderText="Select start date"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
              />
              <span className="mx-4 text-gray-500">to</span>
              <DatePicker
                selected={toDate}
                onChange={(date: any) => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                placeholderText="Select end date"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
              />
            </div>
            <div className="flex justify-end mt-4">
              <button
                className="px-3 py-1 border border-gray-300 rounded-lg text-fontColor font-medium bg-white hover:bg-gray-100 mr-2"
                onClick={() => setShowDownloadDatePicker(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 border border-gray-300 rounded-lg  font-medium bg-blue-500 text-white hover:bg-blue-600"
                onClick={() => handleDownloadCSV(fromDate, toDate)}
                disabled={!fromDate || !toDate}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-[500px]">
            <h3 className="text-lg font-semibold text-fontBlack mb-4">
              Custom Date Range
            </h3>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-4">
                <DatePicker
                  selected={fromDate}
                  onChange={(date: any) => handleDateChange(date, toDate)}
                  selectsStart
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="Select start date"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                />
                <span className="text-gray-500">to</span>
                <DatePicker
                  selected={toDate}
                  onChange={(date: any) => handleDateChange(fromDate, date)}
                  selectsEnd
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="Select end date"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-lg text-fontColor font-medium bg-white hover:bg-gray-100"
                  onClick={handleCloseCustomDateModal}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600"
                  onClick={() => {
                    if (fromDate && toDate) {
                      handleDateChange(fromDate, toDate);
                      setShowCustomDateModal(false);
                    }
                  }}
                  disabled={!fromDate || !toDate}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmation && (
        <ConfirmationDialog
          message={confirmationMessage}
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
        />
      )}

      <div className="border border-border3 bg-white rounded-lg overflow-auto shadow-1 -mt-3">
        <table className="min-w-full border-collapse">
          <thead className="relative">
            <tr className="bg-tableHeadBg border-b-2 border-border3 m-2">
              {columns?.map((column, index) => (
                <th
                  key={column.field}
                  className={`relative p-2 text-headGrey border-r border-border3 text-[12px] font-normal last:border-r-0 ${
                    index === columns.length - 1 ? "sticky-column" : ""
                  } ${column.renderHeader ? "py-3 overflow-visible" : ""}`}
                  style={column.renderHeader ? { zIndex: 10, position: 'relative' } : {}}
                >
                  {column.renderHeader ? column.renderHeader() : column.headerName?.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row) => (
              <tr key={getRowId(row)} className="border-b border-gray-200">
                {columns.map((column, index) => (
                  <td
                    key={column.field}
                    className={`p-2 text-sm last:border-r-0 border-r border-border3 text-center font-medium  h-[51px] ${
                      index === columns.length - 1 ? "sticky-column" : ""
                    } ${
                      index === 0 ||
                      index === 1 ||
                      column.headerName === "Status"
                        ? "text-fontBlack"
                        : "text-headGrey"
                    }`}
                  >
                    {column.renderCell
                      ? column.renderCell({ value: row[column.field], row })
                      : typeof row[column.field] === "string"
                      ? row[column.field]
                          .split(" ")
                          .map((word: string, wordIndex: number) => {
                            if (word.includes("http")) {
                              return (
                                <span key={wordIndex}>
                                  {wordIndex > 0 && " "}
                                  <a
                                    href={word}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-500 hover:text-blue-700 underline"
                                  >
                                    {word}
                                  </a>
                                </span>
                              );
                            }
                            return (
                              <span key={wordIndex}>
                                {wordIndex > 0 && " "}
                                {word}
                              </span>
                            );
                          })
                      : row[column.field]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows?.length === 0 && rows && (
          <>
            <div className="p-4 text-center">No results found.</div>
          </>
        )}
      </div>

      <div className="flex justify-end items-center mt-4 m-4">
        <button
          className="px-3 py-1 border border-gray-300 rounded-lg mr-1 text-fontColor font-medium bg-white hover:bg-gray-100"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <p className="text-fontBlack">&laquo;</p>
        </button>
        <button
          className="px-3 py-1 border border-gray-300 rounded-lg mr-1 text-fontColor font-medium bg-white hover:bg-gray-100"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <p className="text-fontBlack">&lt;</p>
        </button>
        {/* Pagination buttons: show max 5 */}
        {(() => {
          const maxButtons = 5;
          let startPage = 1;
          let endPage = totalPages;
          if (totalPages > maxButtons) {
            if (currentPage <= 3) {
              startPage = 1;
              endPage = maxButtons;
            } else if (currentPage + 2 >= totalPages) {
              startPage = totalPages - maxButtons + 1;
              endPage = totalPages;
            } else {
              startPage = currentPage - 2;
              endPage = currentPage + 2;
            }
          }
          return Array.from({ length: endPage - startPage + 1 }, (_, idx) => {
            const page = startPage + idx;
            return (
              <button
                key={page}
                className={`px-3 py-1 border border-gray-300 rounded-lg mr-1 text-fontColor font-medium ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-white hover:bg-gray-100"
                }`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            );
          });
        })()}
        <button
          className="px-3 py-1 border border-gray-300 rounded-lg mr-1 text-fontColor font-medium bg-white hover:bg-gray-100"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <p className="text-fontBlack">&gt;</p>
        </button>
        <button
          className="px-3 py-1 border border-gray-300 rounded-lg mr-1 text-fontColor font-medium bg-white hover:bg-gray-100"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <p className="text-fontBlack">&raquo;</p>
        </button>
      </div>
    </div>
  );
};

export default CommonDataGrid;
